<template>
  <v-container
    fluid
    class="text--primary"
    style="min-height: 590px"
  >
    <h2>{{ $t('title.opening_hours') }}</h2>
    <p>
      {{ $t('help.onboarding.opening_hours') }}
    </p>
    <v-alert
      class="text-body-2"
      dense
      outlined
      :dark="false"
      border="left"
      color="primary"
      type="info"
    >
      <i18n
        path="help.onboarding.opening_hours_gaps"
        tag="span"
      >
        <span class="text-weight-medium">{{ $t('title.settings') }}→{{ $t('title.opening_hours') }}</span>.
      </i18n>
    </v-alert>
    <v-divider class="py-5" />
    <template v-for="(day, i) in days">
      <div
        :key="`day_${i}`"
        class="row py-0 d-flex pb-2"
      >
        <v-col class="pa-0 d-flex align-center flex-grow-0 pl-5">
          <v-checkbox
            :input-value="open[day.value]"
            class="pa-0 ma-0"
            hide-details
            @change="(open) => changeOpen(day.value, open)"
          />
        </v-col>
        <v-col
          cols="1"
          :class="['py-0 py-0 d-flex align-center', `${open[day.value] ? 'text--primary' : 'text--secondary'}`]"
        >
          <span class="font-weight-bold">{{ day.text }}</span>
        </v-col>
        <v-col class="py-0 py-0 flex-grow-1">
          <v-range-slider
            :value="slots[day.value]"
            :disabled="!open[day.value]"
            :min="24"
            :max="96"
            :step="slotStep"
            track-color="grey lighten-1"
            hide-details
            @input="(bounds) => changeSlot(day.value, bounds)"
          />
        </v-col>
        <v-col
          cols="2"
          :class="['pa-0 ma-0 d-flex align-center', `${open[day.value] ? 'text--primary' : 'text--secondary'}`]"
          style="min-width: 120px"
        >
          <template v-if="open[day.value]">
            {{ timeFromSlot(slots[day.value][0]) }} - {{ timeFromSlot(slots[day.value][1]) }}
          </template>
          <template v-else>
            {{ $t('label.closed') }}
          </template>
        </v-col>
      </div>
    </template>
    <v-divider class="mt-5 mb-4" />
    <v-select
      v-model="bookingInterval"
      :items="bookingIntervals"
      :label="$t('label.booking_interval')"
      @input="bookingIntervalChanged"
    />
  </v-container>
</template>

<script lang="ts">
import OpeningHour from '@/model/OpeningHour';
import { niceTimeTextFromSlot, weekdayNumberFromDateDay } from '@/services/time-utils';
import { performSaveAction } from '@/services/vue-utils';
import Vue from 'vue';
import {
  guessIntervalFromHoursInWeekdays,
  hoursFromSimpleHoursInWeekdays, openingHoursToSimpleHoursInWeekdays, roundedSimpleHoursInWeekdays, SimpleHour,
} from '@/services/hours-utils';
import { isModelEqualToModel } from '@/model/model-utils';

export default Vue.extend({
  name: 'WelcomeStepHours',
  components: { },
  props: { },
  data() {
    return {
      oldHours: [] as OpeningHour[],
      bookingInterval: 15,
      bookingIntervals: [
        { text: this.$i18n.t('label.15_minutes'), value: 15 },
        { text: this.$i18n.t('label.30_minutes'), value: 30 },
      ],
      simpleHoursInWeekdays: [] as SimpleHour[], // in weekdays - starting from 1 (Mon === 1, Tue === 2, ....)
      unwatch: Function as (() => void),
    };
  },
  computed: {
    days(): {value: number, text: string}[] {
      return this.$localized.weekdays();
    },
    isChanged(): boolean {
      return !isModelEqualToModel(this.hours, this.oldHours);
    },
    is30minInterval(): boolean {
      return this.bookingInterval === 30;
    },
    slotStep(): number {
      return this.bookingInterval === 30 ? 2 : 1;
    },
    open(): boolean[] {
      return Array.from({ length: this.days.length }, (x, day) => {
        const weekdayNumber = weekdayNumberFromDateDay(day);
        const sh = this.simpleHoursInWeekdays[weekdayNumber] ?? new SimpleHour();
        return sh.isOpen;
      });
    },
    slots(): number[][] {
      return Array.from({ length: this.days.length }, (x, day) => {
        const weekdayNumber = weekdayNumberFromDateDay(day);
        const sh = this.simpleHoursInWeekdays[weekdayNumber] ?? new SimpleHour();
        return [sh.beginSlot, sh.endSlot];
      });
    },
    hours(): OpeningHour[] {
      return hoursFromSimpleHoursInWeekdays(this.simpleHoursInWeekdays, this.is30minInterval);
    },
  },
  watch: { },
  mounted() {
    this.reset();
  },
  created() {
    this.reset();
    this.unwatch = this.$tstore.watch((state, getters) => getters.isLoaded, (newValue, oldValue) => { this.reset(); });
  },
  beforeDestroy() {
    this.unwatch();
  },

  methods: {
    reset() {
      this.oldHours = this.$tstore.state.settings.openingHours;
      const hourMaps = this.$tstore.getters.openingHoursInWeekdaysByTimeIndex as Map<number, OpeningHour>[];

      const simpleHoursInWeekdays = openingHoursToSimpleHoursInWeekdays(hourMaps, this.is30minInterval);
      this.simpleHoursInWeekdays = simpleHoursInWeekdays;

      const bookingInterval = guessIntervalFromHoursInWeekdays(hourMaps);
      this.bookingInterval = bookingInterval;
    },
    changeSlot(day: number, slots: number[]) {
      const weekdayNumber = weekdayNumberFromDateDay(day);
      const sh = this.simpleHoursInWeekdays[weekdayNumber];
      [sh.beginSlot, sh.endSlot] = [...slots];
    },
    changeOpen(day: number, open: boolean) {
      const weekdayNumber = weekdayNumberFromDateDay(day);
      const sh = this.simpleHoursInWeekdays[weekdayNumber];
      sh.isOpen = open;
    },
    bookingIntervalChanged(interval: number) {
      if (!this.is30minInterval) return;
      this.simpleHoursInWeekdays = roundedSimpleHoursInWeekdays(this.simpleHoursInWeekdays);
    },
    // eslint-disable-next-line vue/no-unused-properties
    async submit() {
      if (!this.isChanged) {
        this.$emit('advance');
        return;
      }

      const entities = this.hours;

      const ok = await performSaveAction(
        undefined,
        async () => this.$tstore.dispatch('sendOpeningHours', { entities }),
      );
      if (ok) {
        this.reset();
        this.$emit('advance');
      }
    },
    timeFromSlot(slot: number): string {
      return this.$localized.hourMinTextFrom24hr(niceTimeTextFromSlot(slot)) || '';
    },
  },
});
</script>
